import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { LoadingScreen, BottomTabBar } from './components/ui';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { TierGateProvider } from './components/TierGateInterceptor';
import UpgradeBanner from './components/UpgradeBanner';
import PushNotificationPrompt from './components/PushNotificationPrompt';

// Lazy-loaded pages — each becomes its own chunk, loaded on navigation
const Welcome = lazy(() => import('./pages/Welcome'));
const GetStarted = lazy(() => import('./pages/GetStarted'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const SportSelection = lazy(() => import('./pages/onboarding/SportSelection'));
const ConversationalOnboarding = lazy(() => import('./pages/onboarding/ConversationalOnboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const GuestDashboard = lazy(() => import('./pages/GuestDashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const MyStory = lazy(() => import('./pages/MyStory'));
const Health = lazy(() => import('./pages/Health'));
const Create = lazy(() => import('./pages/Create'));
const Learn = lazy(() => import('./pages/Learn'));
const More = lazy(() => import('./pages/More'));
const ArticleView = lazy(() => import('./pages/learn/ArticleView'));
const EducationContentView = lazy(() => import('./pages/learn/EducationContentView'));
const ChecklistView = lazy(() => import('./pages/learn/ChecklistView'));
const StateGuideView = lazy(() => import('./pages/learn/StateGuideView'));

const BodyHub = lazy(() => import('./pages/body/BodyHub'));
const TrainingLogForm = lazy(() => import('./pages/body/TrainingLogForm'));
const NutritionLogForm = lazy(() => import('./pages/body/NutritionLogForm'));
const BodyMetricsForm = lazy(() => import('./pages/body/BodyMetricsForm'));
const RecoveryLogForm = lazy(() => import('./pages/body/RecoveryLogForm'));
const PerformanceDashboard = lazy(() => import('./pages/body/PerformanceDashboard'));

const ConnectLayout = lazy(() => import('./pages/connect/ConnectLayout'));
const ConnectFeed = lazy(() => import('./pages/connect/ConnectFeed'));
const ConnectDiscover = lazy(() => import('./pages/connect/ConnectDiscover'));
const ConnectEvents = lazy(() => import('./pages/connect/ConnectEvents'));
const ConnectEventDetail = lazy(() => import('./pages/connect/ConnectEventDetail'));
const ConnectProfileEditor = lazy(() => import('./pages/connect/ConnectProfileEditor'));
const ConnectAthleteProfile = lazy(() => import('./pages/connect/ConnectAthleteProfile'));

const VisionJournal = lazy(() => import('./pages/journal/VisionJournal'));
const MyWhy = lazy(() => import('./pages/journal/MyWhy'));
const StoryBank = lazy(() => import('./pages/journal/StoryBank'));
const StoryEditor = lazy(() => import('./pages/journal/StoryEditor'));
const Reflections = lazy(() => import('./pages/journal/Reflections'));
const ReflectionEditor = lazy(() => import('./pages/journal/ReflectionEditor'));
const VisionBoard = lazy(() => import('./pages/journal/VisionBoard'));
const MyVoice = lazy(() => import('./pages/journal/MyVoice'));

const DigestPage = lazy(() => import('./pages/digest/DigestPage'));

const ContentStudioLayout = lazy(() => import('./pages/content-studio/ContentStudioLayout'));
const QueueView = lazy(() => import('./pages/content-studio/QueueView'));
const CalendarView = lazy(() => import('./pages/content-studio/CalendarView'));
const ApprovalBin = lazy(() => import('./pages/content-studio/ApprovalBin'));

const MyBusiness = lazy(() => import('./pages/business/MyBusiness'));
const DealPipeline = lazy(() => import('./pages/business/DealPipeline'));
const DealDetail = lazy(() => import('./pages/business/DealDetail'));
const FinancialDashboard = lazy(() => import('./pages/business/FinancialDashboard'));
const ScholarshipHub = lazy(() => import('./pages/business/ScholarshipHub'));
const ScholarshipDetail = lazy(() => import('./pages/business/ScholarshipDetail'));

const HealthVaultHome = lazy(() => import('./pages/health/HealthVaultHome'));
const MedicalRecords = lazy(() => import('./pages/health/MedicalRecords'));
const InjuryTracker = lazy(() => import('./pages/health/InjuryTracker'));
const Medications = lazy(() => import('./pages/health/Medications'));
const CareTeamPage = lazy(() => import('./pages/health/CareTeamPage'));
const Appointments = lazy(() => import('./pages/health/Appointments'));

const CoachingHome = lazy(() => import('./pages/coaching/CoachingHome'));
const CoachProfilePage = lazy(() => import('./pages/coaching/CoachProfile'));
const MyCoaching = lazy(() => import('./pages/coaching/MyCoaching'));
const CoachApply = lazy(() => import('./pages/coaching/CoachApply'));

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminScholarships = lazy(() => import('./pages/admin/AdminScholarships'));
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'));
const AdminCoachApplications = lazy(() => import('./pages/admin/AdminCoachApplications'));
const AdminFlaggedReviews = lazy(() => import('./pages/admin/AdminFlaggedReviews'));
const AdminRevenue = lazy(() => import('./pages/admin/AdminRevenue'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));

const SettingsHome = lazy(() => import('./pages/settings/SettingsHome'));
const BillingPage = lazy(() => import('./pages/settings/BillingPage'));
const NotificationsPage = lazy(() => import('./pages/settings/NotificationsPage'));
const PrivacyPage = lazy(() => import('./pages/settings/PrivacyPage'));
const LinkedAccountsPage = lazy(() => import('./pages/settings/LinkedAccountsPage'));

// Layout component for main app with bottom nav
function AppLayout({ children }) {
  const { user } = useAuth();
  return (
    <>
      <UpgradeBanner tier={user?.subscriptionTier || 'FREE'} />
      {children}
      <PushNotificationPrompt />
      <BottomTabBar />
    </>
  );
}

export default function App() {
  const { loading, isAuthenticated, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <TierGateProvider currentTier={user?.subscriptionTier || 'FREE'}>
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      {/* PUBLIC LANDING - Welcome screen */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Welcome />
          )
        }
      />

      {/* PUBLIC - User type selection */}
      <Route
        path="/get-started"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <GetStarted />
          )
        }
      />

      {/* PUBLIC - Sport selection (pre-registration guest browsing) */}
      <Route
        path="/select-sport"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <SportSelection />
          )
        }
      />

      {/* PUBLIC - Guest Dashboard (browse without auth) */}
      <Route path="/explore" element={<GuestDashboard />} />

      {/* Auth routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login />
          )
        }
      />
      <Route
        path="/register"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Register />
          )
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Conversational onboarding (optional, user-initiated) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <ConversationalOnboarding />
          </ProtectedRoute>
        }
      />

      {/* Main app routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-story"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MyStory />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/checkins"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Health />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Health Vault */}
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <AppLayout>
              <HealthVaultHome />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/records"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MedicalRecords />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/injuries"
        element={
          <ProtectedRoute>
            <AppLayout>
              <InjuryTracker />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/medications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Medications />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/care-team"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CareTeamPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/health/appointments"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Appointments />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Vision Journal routes */}
      <Route
        path="/journal"
        element={
          <ProtectedRoute>
            <AppLayout>
              <VisionJournal />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        {/* Default redirect to purpose */}
        <Route index element={<Navigate to="/journal/purpose" replace />} />
        <Route path="purpose" element={<MyWhy />} />
        <Route path="stories" element={<StoryBank />} />
        <Route path="stories/new" element={<StoryEditor />} />
        <Route path="stories/:id" element={<StoryEditor />} />
        <Route path="reflections" element={<Reflections />} />
        <Route path="reflections/new" element={<ReflectionEditor />} />
        <Route path="reflections/:id" element={<ReflectionEditor />} />
        <Route path="vision-board" element={<VisionBoard />} />
        <Route path="voice" element={<MyVoice />} />
      </Route>

      {/* Create - Content Studio */}
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ContentStudioLayout />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/create/queue" replace />} />
        <Route path="queue" element={<QueueView />} />
        <Route path="calendar" element={<CalendarView />} />
        <Route path="approval" element={<ApprovalBin />} />
        <Route path="new" element={<Create />} />
      </Route>

      {/* Body & Performance */}
      <Route
        path="/body"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BodyHub />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/body/training/new"
        element={
          <ProtectedRoute>
            <AppLayout>
              <TrainingLogForm />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/body/nutrition/new"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NutritionLogForm />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/body/metrics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BodyMetricsForm />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/body/recovery/new"
        element={
          <ProtectedRoute>
            <AppLayout>
              <RecoveryLogForm />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/body/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PerformanceDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Learn - Education Hub */}
      <Route
        path="/learn"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Learn />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ArticleView />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/content/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <EducationContentView />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/checklist/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ChecklistView />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn/state/:stateCode"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StateGuideView />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Digest */}
      <Route
        path="/digest/latest"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DigestPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/digest/:weekStart"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DigestPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* More */}
      <Route
        path="/more"
        element={
          <ProtectedRoute>
            <AppLayout>
              <More />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Connect */}
      <Route
        path="/connect"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ConnectLayout />
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<ConnectFeed />} />
        <Route path="discover" element={<ConnectDiscover />} />
        <Route path="events" element={<ConnectEvents />} />
      </Route>
      <Route
        path="/connect/events/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div className="min-h-screen bg-gradient-dark safe-top px-4 pb-20 pt-4">
                <ConnectEventDetail />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/connect/profile/me"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div className="min-h-screen bg-gradient-dark safe-top px-4 pb-20 pt-4">
                <ConnectProfileEditor />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/connect/athletes/:userId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <div className="min-h-screen bg-gradient-dark safe-top px-4 pb-20 pt-4">
                <ConnectAthleteProfile />
              </div>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* My Business routes */}
      <Route
        path="/business"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MyBusiness />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/pipeline"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DealPipeline />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/deals/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DealDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FinancialDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/scholarships"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ScholarshipHub />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/scholarships/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ScholarshipDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Coaching Marketplace */}
      <Route
        path="/coaching"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CoachingHome />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaching/:coachId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CoachProfilePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaching/my-coaching"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MyCoaching />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaching/apply"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CoachApply />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <SettingsHome />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <AppLayout>
              <BillingPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <NotificationsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/privacy"
        element={
          <ProtectedRoute>
            <AppLayout>
              <PrivacyPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/linked-accounts"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LinkedAccountsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="/admin/users" replace />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="stats" element={<AdminStats />} />
        <Route path="revenue" element={<AdminRevenue />} />
        <Route path="scholarships" element={<AdminScholarships />} />
        <Route path="campaigns" element={<AdminCampaigns />} />
        <Route path="coaches" element={<AdminCoachApplications />} />
        <Route path="reviews" element={<AdminFlaggedReviews />} />
        <Route path="jobs" element={<AdminJobs />} />
      </Route>

      {/* Catch all - redirect to dashboard or landing */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </Routes>
    </Suspense>
    </TierGateProvider>
  );
}
